import { useState, useEffect } from 'react';
import {
  Card, CardContent, Typography, Button, Box, TextField,
  FormControl, FormLabel,Select, MenuItem, Alert, Slider
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSession } from '../context/SessionContext';
import { surveyApi } from '../utils/api';
import { SurveyDefinition, PreSurveyResponses, SurveyQuestion } from '../types';

export default function PreSurveyPage() {
  const { updateStatus } = useSession();

  const { data: surveyTypes } = useQuery({
    queryKey: ['survey', 'for-assessment'],
    queryFn: () => surveyApi.getSurveyTypes(),
  });

  // Auto-skip if no pre survey is linked to this assessment
  useEffect(() => {
    if (surveyTypes && !surveyTypes.survey_types.includes('pre')) {
      updateStatus('directions');
    }
  }, [surveyTypes, updateStatus]);

  const { data: definition } = useQuery<SurveyDefinition>({
    queryKey: ['survey', 'definition', 'pre'],
    queryFn: () => surveyApi.getDefinition('pre'),
    enabled: surveyTypes?.survey_types.includes('pre') ?? false,
  });
  const [responses, setResponses] = useState<PreSurveyResponses>({});
  const [error, setError] = useState('');

  const handleChange = (questionId: string, value: string | number) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const submitMutation = useMutation({
    mutationFn: (r: PreSurveyResponses) => surveyApi.submitPreSurvey(r),
    onSuccess: () => updateStatus('directions'),
    onError: () => setError('Failed to submit survey. Please try again.'),
  });

  const handleSubmit = () => {
    setError('');
    submitMutation.mutate(responses);
  };

  const renderQuestion = (question: SurveyQuestion, index: number) => {
    const questionNumber = index + 1;
    
    switch (question.type) {
      case 'scale':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto' }}>
            <FormLabel id={`question-${question.id}-label`}>
              {questionNumber}. {question.question}
            </FormLabel>
            <Box sx={{ px: 2, pt: 2 }}>
              <Slider
                value={responses[question.id as keyof PreSurveyResponses] as number || question.min}
                onChange={(_, value) => handleChange(question.id, value as number)}
                min={question.min}
                max={question.max}
                step={1}
                marks={[
                  { value: question.min!, label: question.min_label },
                  { value: question.max!, label: question.max_label },
                ]}
                valueLabelDisplay="auto"
                aria-labelledby={`question-${question.id}-label`}
              />
            </Box>
          </FormControl>
        );

      case 'text':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto' }}>
            <FormLabel id={`question-${question.id}-label`} sx={{ mb: 1, color: 'text.primary' }}>
              {questionNumber}. {question.question}
            </FormLabel>
            <TextField
              value={responses[question.id as keyof PreSurveyResponses] || ''}
              onChange={(e) => handleChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              variant="outlined"
              inputProps={{
                'aria-labelledby': `question-${question.id}-label`,
              }}
            />
          </FormControl>
        );

      case 'textarea':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto' }}>
            <FormLabel id={`question-${question.id}-label`} sx={{ mb: 1, color: 'text.primary' }}>
              {questionNumber}. {question.question}
            </FormLabel>
            <TextField
              value={responses[question.id as keyof PreSurveyResponses] || ''}
              onChange={(e) => handleChange(question.id, e.target.value)}
              placeholder={question.placeholder}
              multiline
              rows={3}
              variant="outlined"
              inputProps={{
                'aria-labelledby': `question-${question.id}-label`,
              }}
            />
          </FormControl>
        );

      case 'select':
        return (
          <FormControl key={question.id} sx={{ width: '80%', mb: 4, mx: 'auto' }}>
            <FormLabel id={`question-${question.id}-label`} sx={{ mb: 1, color: 'text.primary' }}>
              {questionNumber}. {question.question}
            </FormLabel>
            <Select
              labelId={`question-${question.id}-label`}
              value={responses[question.id as keyof PreSurveyResponses] || ''}
              onChange={(e) => handleChange(question.id, e.target.value)}
              aria-labelledby={`question-${question.id}-label`}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <em style={{ color: '#999' }}>Select an option</em>;
                }
                return question.options?.find(opt => opt.value === selected)?.label || selected;
              }}
            >
              <MenuItem value="" disabled>
                <em>Select an option</em>
              </MenuItem>
              {question.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      default:
        return null;
    }
  };

  if (!definition) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Loading survey...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 8 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          {definition.title}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Estimated time: {definition.estimated_time}
        </Typography>

        <Typography variant="body2" paragraph sx={{fontSize: '1rem', mb: 5 }}>
          {definition.instructions}
        </Typography>

        <Box component="form" noValidate >
          {definition.questions.map((question, index) => renderQuestion(question, index))}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} role="alert">
            {error}
          </Alert>
        )}

        <Box textAlign="center" sx={{ mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitMutation.isPending}
            sx={{ px: 6 }}
          >
            {submitMutation.isPending ? 'Submitting...' : 'Next'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
